import { useState, useEffect } from "react";
import EmployeeLayout from "../../layouts/EmployeeLayout";
import { getEmployeeProgress } from "../../api/employeeService";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

/* ─────────────────────────────────────────────
   EmployeeDailyProgress — monthly activity report
   Props:
     employeeId (string|undefined)
       • undefined / null  → reads logged-in employee's own data
       • provided          → MANAGER / ADMIN viewing a specific employee
     embedMode (bool, default false)
       • false → normal standalone page with EmployeeLayout + back button
       • true  → renders content only (no layout, no back button)
                 used when embedded inside AdminUserDetails
───────────────────────────────────────────── */
function EmployeeActivityProgress({ employeeId, embedMode = false }) {
  const [month, setMonth] = useState(
    new Date().toLocaleString("default", { month: "long" })
  );
  const [year, setYear] = useState(null);
  const [activities, setActivities] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [loadingYears, setLoadingYears] = useState(true);
  const [fetching, setFetching] = useState(false);

  // Always call useNavigate — hooks must never be called conditionally
  const navigate = useNavigate();

  /* ── fetch whenever month / year / employeeId change ── */
  useEffect(() => {
    const fetchProgress = async () => {
      setFetching(true);
      try {
        const params = { month, year };
        if (employeeId) params.employee_id = employeeId;

        const res = await getEmployeeProgress(params);
        const yrs = Array.isArray(res.data.available_years)
          ? res.data.available_years
          : [];

        setAvailableYears(yrs);

        if (!year || !yrs.includes(Number(year))) {
          setYear(yrs.length > 0 ? yrs[0] : new Date().getFullYear());
        }

        setActivities(res.data.activities || []);
      } catch (err) {
        console.error(err);
        setAvailableYears([new Date().getFullYear()]);
        setYear(new Date().getFullYear());
        setActivities([]);
      } finally {
        setLoadingYears(false);
        setFetching(false);
      }
    };

    fetchProgress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, month, year]);

  /* ── CSV download ── */
  const downloadCSV = () => {
    if (!activities.length) return;

    const headers = ["Date", "Activity Name", "Description", "Duration (hrs)"];
    const rows = activities.map((act) => [
      act.activity_date,
      act.activity_name,
      act.description || "-",
      act.duration_hours,
    ]);

    const csv =
      "data:text/csv;charset=utf-8," +
      [headers, ...rows]
        .map((r) => r.map((v) => `"${v}"`).join(","))
        .join("\n");

    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csv));
    link.setAttribute(
      "download",
      `EmployeeProgress_${employeeId || "me"}_${month}_${year}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /* ── navigate back — only used in normal mode ── */
  const handleBack = () => {
    navigate("/employee/activity", { state: { defaultTab: "profile" } });
  };

  const MONTHS = [
    "January", "February", "March", "April",
    "May", "June", "July", "August",
    "September", "October", "November", "December",
  ];

  /* ════════════════════════════════════════════
     SHARED CONTENT — same JSX for both modes
  ════════════════════════════════════════════ */
  const content = (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="w-full"
    >
      {/* ── HEADER ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg flex-shrink-0">
            <span className="text-xl sm:text-2xl">🗂️</span>
          </div>
          <div>
            <h2 className="text-lg sm:text-2xl font-bold text-white">
              Employee Activity Report
            </h2>
            <p className="text-white/60 text-xs sm:text-sm">
              {embedMode ? "Monthly activity progress" : "View your activity progress by month"}
            </p>
          </div>
        </div>

        <button
          onClick={downloadCSV}
          disabled={activities.length === 0}
          className="self-start sm:self-auto px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold shadow transition"
        >
          ⬇ Download Report
        </button>
      </div>

      {/* ── FILTERS ── */}
      <div className="flex flex-wrap gap-3 justify-start sm:justify-end mb-6">
        {/* Month */}
        <div className="flex flex-col text-sm">
          <label className="text-white mb-1">Month</label>
          <select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="bg-black/40 border border-white/20 rounded-lg px-3 sm:px-4 py-2 text-white backdrop-blur-md appearance-none focus:outline-none focus:ring-2 focus:ring-white/30 text-sm min-w-[130px]"
          >
            <option value="" className="bg-black text-white">All Months</option>
            {MONTHS.map((m) => (
              <option key={m} value={m} className="bg-black text-white">{m}</option>
            ))}
          </select>
        </div>

        {/* Year */}
        <div className="flex flex-col text-sm">
          <label className="text-white mb-1">Year</label>
          <select
            value={year || ""}
            onChange={(e) => setYear(e.target.value)}
            disabled={loadingYears}
            className="bg-black/40 border border-white/20 rounded-lg px-3 sm:px-4 py-2 text-white backdrop-blur-md appearance-none focus:outline-none focus:ring-2 focus:ring-white/30 text-sm min-w-[90px]"
          >
            {loadingYears ? (
              <option>Loading...</option>
            ) : (
              availableYears.map((y) => (
                <option key={y} value={y} className="bg-black text-white">{y}</option>
              ))
            )}
          </select>
        </div>
      </div>

      {/* ── TABLE ── */}
      <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden shadow-2xl w-full">
        <div className="overflow-x-auto">
          <table className="w-full text-xs sm:text-sm border-collapse min-w-[500px]">
            <thead className="bg-gradient-to-r from-slate-700 to-slate-800">
              <tr>
                {["Date", "Activity Name", "Description", "Duration"].map((h) => (
                  <th
                    key={h}
                    className="px-3 sm:px-4 py-3 sm:py-4 text-white text-center whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {fetching ? (
                <tr>
                  <td colSpan="4" className="text-center py-8 text-white/50 text-sm">
                    <span className="animate-pulse">Loading…</span>
                  </td>
                </tr>
              ) : activities.length > 0 ? (
                activities.map((act, idx) => (
                  <motion.tr
                    key={act.id ?? idx}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: idx * 0.03 }}
                    className="text-center border-b border-white/5 hover:bg-white/5 transition"
                  >
                    <td className="px-3 sm:px-4 py-3 sm:py-4 text-white whitespace-nowrap">
                      {act.activity_date}
                    </td>
                    <td className="px-3 sm:px-4 py-3 sm:py-4 text-white">
                      {act.activity_name}
                    </td>
                    <td className="px-3 sm:px-4 py-3 sm:py-4 text-white/70">
                      {act.description || "-"}
                    </td>
                    <td className="px-3 sm:px-4 py-3 sm:py-4 text-white whitespace-nowrap">
                      {act.duration_hours} hr
                    </td>
                  </motion.tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center py-8 sm:py-6 text-white/60 text-sm">
                    No activities found for this month/year.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── BACK BUTTON — only in normal (non-embed) mode ── */}
      {!embedMode && (
        <div className="mt-6 flex justify-start">
          <button
            onClick={handleBack}
            className="px-5 sm:px-6 py-2 rounded-lg bg-gray-600 hover:bg-gray-700 text-white font-semibold text-sm transition"
          >
            ← Back
          </button>
        </div>
      )}
    </motion.div>
  );

  /* ════════════════════════════════════════════
     RENDER
     • embedMode → content only (no EmployeeLayout)
     • normal    → wrapped in EmployeeLayout (original behaviour)
  ════════════════════════════════════════════ */
  if (embedMode) {
    return content;
  }

  return (
    <EmployeeLayout>
      <div className="w-full max-w-[1100px] mx-auto p-3 sm:p-6">
        {content}
      </div>
    </EmployeeLayout>
  );
}

export default EmployeeActivityProgress;